import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Box, Checkbox, IconButton, InputAdornment, TableCell, TableRow } from '@mui/material';
import { ActionPrice, MaterialPriceDifference } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/format';
import PriceSupplementAccordion from './price-supplement-accordion/price-supplement-accordion.component';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { editActionPriceTemplate } from 'apollo/templates/client-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import PriceHistoryButton from 'components/price-requests/history/price-history-button.component';

type ActionBasedClinicActionItemProps = {
    clientCurrency: string;
    clientId: string;
    chainId: string;
    action: ActionPrice;
    materialGroups: MaterialPriceDifference[];
    showAllPriceSupplement: boolean;
    canEditActionList: boolean;
    isTioTandvardPricingEnabled?: boolean;
    onDisplayShowAllButton: (arg: boolean) => void;
    refetchActionList: () => void;
    refetchMaterialGroups: () => void;
};

type MaterialGroup = {
    materialGroupId: string;
    materialGroupName: string;
    materials: {
        material: string;
        materialName: string;
        priceDiff: number | string;
    }[];
};
const ActionBasedClinicActionItem: FC<ActionBasedClinicActionItemProps> = ({
    clientCurrency,
    clientId,
    chainId,
    action,
    materialGroups,
    showAllPriceSupplement,
    canEditActionList,
    isTioTandvardPricingEnabled,
    onDisplayShowAllButton,
    refetchActionList,
    refetchMaterialGroups,
}: ActionBasedClinicActionItemProps) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [isEdit, setIsEdit] = useState(false);
    const [price, setPrice] = useState('');
    const [tiotandvardPrice, setTandvardPrice] = useState('');
    const [materialGroup, setMaterialGroup] = useState<MaterialGroup>();
    const [actionEnabled, setActionEnabled] = useState(false);
    const [deliveryTime, setDeliveryTime] = useState('');
    const lab = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        const materialGroup = materialGroups.find(
            (materialGroup) => materialGroup.materialGroupId?.toString() === action?.materialGroup?.toString(),
        );
        const hasPriceSupplement = materialGroup?.materials.some((material) => material.priceDiff !== null);

        if (hasPriceSupplement) {
            onDisplayShowAllButton(true);
        }
        if (materialGroup) {
            setMaterialGroup(materialGroup as MaterialGroup);
        }
    }, [materialGroups]);

    useEffect(() => {
        if (action?.price) {
            setPrice(action.price.toString());
        }
        if (action.enabled) {
            setActionEnabled(action.enabled);
        }
        if (action?.deliveryTime) {
            setDeliveryTime(action.deliveryTime.toString());
        }
        if (action?.tiotandvardPrice) {
            setTandvardPrice(action.tiotandvardPrice.toString());
        }
    }, [action]);

    const [editActionPrice, { loading }] = useMutation(editActionPriceTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted() {
            refetchActionList();
            refetchMaterialGroups();
        },
    });

    const handlePriceChange = (price: string) => {
        setPrice(price);
    };

    const handleTandvardPriceChange = (tiotandvardPrice: string) => {
        setTandvardPrice(tiotandvardPrice);
    };

    const onEditAction = () => {
        setIsEdit(true);
    };

    const onCancelAction = () => {
        setPrice(action.price?.toString());
        setActionEnabled(action.enabled);
        setDeliveryTime(action.deliveryTime?.toString());
        setIsEdit(false);
        const materialGroup = materialGroups.find(
            (materialGroup) => materialGroup.materialGroupId?.toString() === action?.materialGroup?.toString(),
        );
        if (materialGroup) {
            setMaterialGroup(materialGroup as MaterialGroup);
        }
    };

    const onSaveAction = () => {
        setIsEdit(false);
        editActionPrice({
            variables: {
                clinicId: clientId,
                labId: lab._id,
                actionId: action.action,
                price: parseFloat(price),
                tiotandvardPrice: tiotandvardPrice ? parseFloat(tiotandvardPrice) : null,
                materialGroup: materialGroup,
                actionEnabled: actionEnabled,
                deliveryTime: parseInt(deliveryTime),
            },
        });
    };

    const handleMaterialGroup = (materialGroup: MaterialGroup) => {
        materialGroup?.materials?.forEach((material) => {
            if (material.priceDiff && typeof material.priceDiff === 'string') {
                material.priceDiff = parseFloat(material.priceDiff);
            }
        });
        setMaterialGroup(materialGroup);
    };

    const handleActionEnabledCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        setActionEnabled(event.target.checked);
    };

    const handleDeliveryTimeChange = (deliveryTime: string) => {
        setDeliveryTime(deliveryTime);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <TableRow hover sx={{ cursor: 'pointer' }}>
            {canEditActionList && isEdit ? (
                <TableCell>
                    <Checkbox checked={actionEnabled} onChange={handleActionEnabledCheckbox} />
                </TableCell>
            ) : (
                <TableCell>
                    <ActiveIndicator isActive={action.enabled} />
                </TableCell>
            )}
            <TableCell>{action.mainCategory}</TableCell>
            <TableCell>{action.subCategory.join(', ')}</TableCell>
            <TableCell>{action.number}</TableCell>
            <TableCell>{action.name}</TableCell>
            {canEditActionList && isEdit ? (
                <TableCell align="right">
                    <MonetaryValueInput
                        value={price || '-'}
                        variant="standard"
                        hiddenLabel
                        size="small"
                        onValueChange={handlePriceChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ fontSize: '1px' }}>
                                    {clientCurrency}
                                </InputAdornment>
                            ),
                        }}
                    />
                </TableCell>
            ) : (
                <TableCell>{price && formatCurrency(parseFloat(price), clientCurrency)}</TableCell>
            )}
            {isTioTandvardPricingEnabled &&
                (canEditActionList && isEdit ? (
                    <TableCell align="right">
                        <MonetaryValueInput
                            value={tiotandvardPrice || ''}
                            variant="standard"
                            hiddenLabel
                            size="small"
                            onValueChange={handleTandvardPriceChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ fontSize: '1px' }}>
                                        {clientCurrency}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </TableCell>
                ) : (
                    <TableCell>
                        {tiotandvardPrice && formatCurrency(parseFloat(tiotandvardPrice), clientCurrency)}
                    </TableCell>
                ))}
            {canEditActionList && isEdit ? (
                <TableCell>
                    <NumberOnlyTextField
                        id="deliveryTime"
                        variant="standard"
                        size="small"
                        hasDecimal={false}
                        notAllowZero={true}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {translate('client-directory:DAYS').toLowerCase()}
                                </InputAdornment>
                            ),
                        }}
                        onValueChange={handleDeliveryTimeChange}
                        value={deliveryTime}
                    />
                </TableCell>
            ) : (
                <TableCell>
                    {action.deliveryTime &&
                        (action.deliveryTime > 1
                            ? action.deliveryTime + ' ' + translate('client-directory:DAYS').toLocaleLowerCase()
                            : action.deliveryTime + ' ' + translate('client-directory:DAY').toLocaleLowerCase())}
                </TableCell>
            )}
            <TableCell>
                {materialGroup?.materials.find((material) => material.priceDiff !== null) ? (
                    <PriceSupplementAccordion
                        clientCurrency={clientCurrency}
                        materialGroup={materialGroup}
                        showAllPriceSupplement={showAllPriceSupplement}
                        isEdit={isEdit}
                        canEditActionList={canEditActionList}
                        onMaterialGroupChange={handleMaterialGroup}
                    />
                ) : undefined}
            </TableCell>
            {/* DD-3697: read-only entry point; the editor below is unchanged. */}
            <TableCell>
                <PriceHistoryButton counterpartId={chainId} actionId={action.action} />
            </TableCell>
            {canEditActionList ? (
                <TableCell>
                    {isEdit ? (
                        <Box display="flex">
                            <IconButton
                                disabled={!price || !deliveryTime}
                                onClick={onSaveAction}
                                aria-label="save"
                                size="small"
                            >
                                <CheckIcon />
                            </IconButton>
                            <IconButton onClick={onCancelAction} aria-label="close" size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    ) : (
                        <IconButton onClick={onEditAction} aria-label="edit" size="small">
                            <EditIcon />
                        </IconButton>
                    )}
                </TableCell>
            ) : undefined}
        </TableRow>
    );
};

export default ActionBasedClinicActionItem;
